const Country = require('@/lib/Countries/Country');
const countryList = require('@/lib/Countries/countryList');
const { sortByName } = require('@/functions/arraySorting');

/**
 * @constructor
 *
 * @param {Hydrator} hydrator
 */
const CountryManager = function (
    hydrator
) {
    /**
     * Kept as an array not an object so the order the countries are defined in is preserved.
     *
     * You should not access this array directly as it may not be loaded yet.
     * Instead, use:
     * getCountries().then(function (countries) { // });
     *
     * @private
     *
     * @type {Country[]}
     */
    this.countries = hydrator.hydrateArray(
        countryList,
        () => new Country()
    );

    // Sort the countries by name.
    sortByName(this.countries);
};

CountryManager.prototype = {
    getCountries() {
        return this.countries;
    },

    /**
     * @param {string} code
     * @return {?Country}
     */
    findByCode(code) {
        return this.findByData({ code });
    },

    /**
     * Return a country that matches either a countryCode or countryID property in the supplied
     * object of data. The data object is used instead of simple arguments so we can match various forms of data we
     * know about.
     *
     * Data can contain any of these properties to look up:
     * code / countryCode / country (2 char string)
     * countryID / ID (number)
     * country (object containing code/countryCode/etc. as above)
     *
     * @param {object} data
     *
     * @return {?Country}
     */
    findByData(data) {
        if (!data) {
            return null;
        }

        // Parse the data object to try and find a 2 letter code or country name string.
        let codeOrName = null;
        if (data.hasOwnProperty('code') && data.code) {
            codeOrName = data.code;
        } else if (data.hasOwnProperty('countryCode') && data.countryCode) {
            codeOrName = data.countryCode;
        } else if (data.hasOwnProperty('country') && data.country && typeof data.country === 'string') {
            codeOrName = data.country;
        } else if (data.hasOwnProperty('country') && data.country && typeof data.country === 'object') {
            // It may already be a country object {country: Country}.
            // Try running findByData with that in case it has the properties we are looking for.
            return this.findByData(data.country);
        }

        // Parse the data object to find a country ID.
        let id = null;
        if (data.hasOwnProperty('countryID')) {
            id = data.countryID;
        } else if (data.hasOwnProperty('ID')) {
            id = data.ID;
        }

        if (!codeOrName && !id) {
            return null;
        }

        // Find a country that matches the country or ID.
        for (let i = 0; i < this.countries.length; i++) {
            if (codeOrName && (this.countries[i].code === codeOrName || this.countries[i].name === codeOrName)) {
                return this.countries[i];
            }

            if (id && this.countries[i].ID === id) {
                return this.countries[i];
            }
        }

        return null;
    },
};

module.exports = CountryManager;
